import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import GlobalTooltip from './components/GlobalTooltip'
import { ProtectedRoute, AdminRoute } from './components/ProtectedRoute'

// Pages publiques
import Login from './pages/Login'
import Pending from './pages/Pending'
import Blocked from './pages/Blocked'

// Pages sans sidebar
import Projects from './pages/Projects'
import SnapshotView from './pages/SnapshotView'
import PublicSnapshotView from './pages/PublicSnapshotView'
import ReportEmbed from './pages/ReportEmbed'
import AuditStart from './pages/public/AuditStart'
import AuditSelectAccount from './pages/public/AuditSelectAccount'

// Pages avec sidebar (AppLayout)
import ProjectOverview from './pages/ProjectOverview'
import AuditOverview from './pages/audit/AuditOverview'
import AuditErrors from './pages/audit/AuditErrors'
import AuditPages from './pages/audit/AuditPages'
import AuditResources from './pages/audit/AuditResources'
import AuditLinks from './pages/audit/AuditLinks'
import AuditChecklist from './pages/audit/AuditChecklist'
import AuditIssueDetail from './pages/audit/AuditIssueDetail'

// Pages existantes (a refondre visuellement dans les sprints suivants)
import Audit from './pages/Audit'
import OnPage from './pages/OnPage'
import Serp from './pages/Serp'
import CoreWebVitals from './pages/performance/CoreWebVitals'
import PageSpeedAudit from './pages/performance/PageSpeedAudit'
import Rankings from './pages/Rankings'
import RankingDetailed from './pages/RankingDetailed'
import RankingDetail from './pages/RankingDetail'
import Analytics from './pages/Analytics'
import Local from './pages/Local'
import Reports from './pages/Reports'
import ReportBuilder from './pages/ReportBuilder'
import ReportThemes from './pages/ReportThemes'
import ReportTemplates from './pages/ReportTemplates'
import ProjectSnapshotsPage from './pages/ProjectSnapshotsPage'
import SnapshotsListPage from './pages/SnapshotsListPage'

// Performance detail
import PerfPageDetail from './pages/performance/PerfPageDetail'

// Settings
import SearchEngines from './pages/settings/SearchEngines'
import DataSources from './pages/settings/DataSources'
import ProjectSettings from './pages/settings/ProjectSettings'
import GoogleAccount from './pages/settings/GoogleAccount'

// AI
import AIAnalyst from './pages/AIAnalyst'
import LlmCampaignList from './pages/llm/LlmCampaignList'
import LlmCampaignDetail from './pages/llm/LlmCampaignDetail'
import LlmAuditDetail from './pages/llm/LlmAuditDetail'

// Admin
import Costs from './pages/Costs'
import GlobalDatabase from './pages/admin/GlobalDatabase'
import AdminUsers from './pages/admin/Users'
import ProjectAccess from './pages/admin/ProjectAccess'

// Legal
import Legal from './pages/Legal'

// 404
import NotFound from './pages/NotFound'

// Placeholder
import { ComingSoon } from './components/ui/ComingSoon'

function ComingSoonPage({ title }: { title: string }) {
  return <ComingSoon title={title} />
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <GlobalTooltip />
      <Routes>
        {/* Pages publiques (sans auth) */}
        <Route path="/login" element={<Login />} />
        <Route path="/pending" element={<Pending />} />
        <Route path="/blocked" element={<Blocked />} />
        <Route path="/legal/:page" element={<Legal />} />
        <Route path="/r/:slug/:token" element={<PublicSnapshotView />} />
        {/* Audit gratuit Google Ads (lead magnet) : flux anonyme sans compte Orion,
            de bout en bout hors auth — start (OAuth), puis sélection du sous-compte
            MCC (/audit-gratuit/selection), puis rapport via slug+token. */}
        <Route path="/audit-gratuit/start" element={<AuditStart />} />
        <Route path="/audit-gratuit/selection" element={<AuditSelectAccount />} />
        {/* Rendu PDF d'un snapshot privé : route HORS ProtectedRoute pour que Playwright
            (sans cookie) puisse charger la page. Les données restent protégées par le
            pdf_token court validé côté serveur (endpoint /render). */}
        <Route path="/snapshots/:id/print" element={<SnapshotView />} />

        {/* Pages protegees — approved uniquement */}
        <Route element={<ProtectedRoute />}>
          <Route path="/projects" element={<Projects />} />
          <Route path="/snapshots/:id" element={<SnapshotView />} />
          {/* Rendu LIVE nu d'un rapport (miniature iframée dans la liste des rapports).
              Authentifié via ProtectedRoute mais SANS AppLayout : pas de nav dans l'iframe. */}
          <Route path="/p/:projectId/reports/:reportId/embed" element={<ReportEmbed />} />

          {/* Pages avec sidebar */}
          <Route path="/p/:projectId" element={<AppLayout />}>
            <Route index element={<ProjectOverview />} />

            {/* Audit */}
            <Route path="audit" element={<AuditOverview />} />
            <Route path="audit/errors" element={<AuditErrors />} />
            <Route path="audit/pages" element={<AuditPages />} />
            <Route path="audit/resources" element={<AuditResources />} />
            <Route path="audit/links" element={<AuditLinks />} />
            <Route path="audit/checklist" element={<AuditChecklist />} />
            <Route path="audit/issues/:issueType" element={<AuditIssueDetail />} />

            {/* Rankings */}
            <Route path="rankings" element={<Rankings />} />
            <Route path="rankings/detailed" element={<RankingDetailed />} />
            <Route path="rankings/keywords" element={<RankingDetailed />} />
            <Route path="rankings/pages" element={<ComingSoonPage title="Pages positionnees" />} />
            <Route path="rankings/:keywordId" element={<RankingDetail />} />

            {/* Performance */}
            <Route path="performance/vitals" element={<CoreWebVitals />} />
            <Route path="performance/pagespeed" element={<PageSpeedAudit />} />
            <Route path="performance/page/:pageId" element={<PerfPageDetail />} />

            {/* Visibilite IA */}
            <Route path="ai/analyst" element={<AIAnalyst />} />
            <Route path="ai/llm" element={<LlmCampaignList />} />
            <Route path="ai/llm/:campaignId" element={<LlmCampaignDetail />} />
            <Route path="ai/llm/:campaignId/:auditId" element={<LlmAuditDetail />} />
            <Route path="ai/overviews" element={<ComingSoonPage title="AI Overviews" />} />
            <Route path="ai/prompts" element={<ComingSoonPage title="Prompts suivis" />} />

            {/* Marketing Local */}
            <Route path="local" element={<Navigate to="insights" replace />} />
            <Route path="local/insights" element={<Local />} />
            <Route path="local/maps" element={<Local />} />
            <Route path="local/reputation" element={<Local />} />
            <Route path="local/posts" element={<Local />} />
            <Route path="local/updates" element={<Local />} />

            {/* Concurrents */}
            <Route path="competitors/added" element={<ComingSoonPage title="Concurrents ajoutes" />} />
            <Route path="competitors/share" element={<ComingSoonPage title="Part de voix" />} />
            <Route path="competitors/serp" element={<ComingSoonPage title="Concurrents SERP" />} />

            {/* Analytics */}
            <Route path="analytics/audience" element={<Analytics />} />
            <Route path="analytics/sources" element={<Analytics />} />
            <Route path="analytics/search" element={<ComingSoonPage title="Visibilite recherche" />} />

            {/* Backlinks */}
            <Route path="backlinks/checker" element={<ComingSoonPage title="Verificateur de backlinks" />} />
            <Route path="backlinks/monitor" element={<ComingSoonPage title="Suivi de backlinks" />} />

            {/* Outils */}
            <Route path="insights" element={<ComingSoonPage title="Insights" />} />
            <Route path="action-plan" element={<ComingSoonPage title="Plan d'actions" />} />
            <Route path="changes" element={<ComingSoonPage title="Suivi des modifications" />} />

            {/* Settings projet */}
            <Route path="settings" element={<ProjectSettings />} />
            <Route path="settings/search-engines" element={<SearchEngines />} />
            <Route path="settings/data-sources" element={<DataSources />} />

            {/* Rapports */}
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:reportId" element={<ReportBuilder />} />
            <Route path="snapshots" element={<ProjectSnapshotsPage />} />
            <Route path="report-templates" element={<ReportTemplates />} />
            <Route path="reports/:reportId/snapshots" element={<SnapshotsListPage />} />

            {/* 404 dans le contexte projet (sidebar conservee) */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Pages hors projet (avec sidebar) */}
          <Route path="/costs" element={<AppLayout />}>
            <Route index element={<Costs />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/settings" element={<AppLayout />}>
            <Route path="google-account" element={<GoogleAccount />} />
            <Route path="api-keys" element={<ComingSoonPage title="Clés API" />} />
            <Route index element={<Navigate to="/settings/google-account" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Thèmes de rapport (bibliothèque globale, hors projet) */}
          <Route path="/themes" element={<AppLayout />}>
            <Route index element={<ReportThemes />} />
            <Route path=":themeId" element={<ReportThemes />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Templates de rapport (bibliothèque globale, hors projet) */}
          <Route path="/report-templates" element={<AppLayout />}>
            <Route index element={<ReportTemplates />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        {/* Pages admin uniquement */}
        <Route element={<AdminRoute />}>
          <Route path="/admin/global-database" element={<AppLayout />}>
            <Route index element={<GlobalDatabase />} />
          </Route>
          <Route path="/admin/users" element={<AppLayout />}>
            <Route index element={<AdminUsers />} />
          </Route>
          <Route path="/admin/project-access" element={<AppLayout />}>
            <Route index element={<ProjectAccess />} />
          </Route>
        </Route>

        {/* Legacy redirects */}
        <Route path="/clients" element={<Navigate to="/projects" replace />} />
        <Route path="/clients/:clientId" element={<Navigate to="/projects" replace />} />
        <Route path="/" element={<Navigate to="/projects" replace />} />

        {/* Catch-all racine : toute URL qui ne tombe dans aucun groupe ci-dessus.
            Rendu hors AppLayout (l'utilisateur n'est pas forcement authentifie),
            avec un lien de retour explicite. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  )
}
